import styled from 'styled-components';

// Same layout as the rebud desktop nav (MainMenuRebud). Fonts and colors are
// overridable per marketplace through headerConfig.desktopNavConfig.styling.
const StyledMainMenuUniversal = styled.div`
  display: none;

  @media (min-width: 860px) {
    display: flex;
    flex: 1;
    align-items: stretch;
    margin: 0 24px;
    min-width: 0;
  }

  ${({ themeProps }) => (themeProps?.fontFamily ? `font-family: ${themeProps.fontFamily};` : '')}

  .rmenu {
    position: relative;
    display: flex;
    flex: 1;
    min-width: 0;
  }

  .rmenu__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    align-items: stretch;
    flex-wrap: wrap;
    row-gap: 8px;
  }

  /* ── Trigger ── */

  .rmenu__trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 16px;
    height: 100%;
    font-size: ${({ themeProps }) => themeProps?.triggerFontSize || '16px'};
    font-weight: 800;
    letter-spacing: 0.08em;
    color: ${({ themeProps, theme }) => themeProps?.triggerColor || themeProps?.color || theme.colors.brandDark};
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.2s;
    line-height: 1;

    &:hover,
    &:focus,
    &.rmenu__trigger--open {
      color: ${({ themeProps, theme }) =>
        themeProps?.triggerHoverColor || themeProps?.hoverColor || theme.colors.brand};
      outline: none;
    }
  }

  .rmenu__chevron {
    width: 10px;
    height: 10px;
    flex-shrink: 0;
    transition: transform 0.2s;
  }

  .rmenu__trigger--open .rmenu__chevron {
    transform: rotate(-180deg);
  }

  /* ── Nav item ── */

  .rmenu__item {
    position: relative;
    display: flex;
    align-items: stretch;
    flex-shrink: 0;
  }

  /*
   * Invisible bridge that covers the gap between the trigger and the dropdown.
   * Without it the mouse briefly leaves the <li> while crossing the gap,
   * firing onMouseLeave and closing the menu before the user reaches it.
   */
  .rmenu__item--dropdown::after,
  .rmenu__item--shop::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    height: 14px;
    background: transparent;
  }

  /* ── Shared dropdown ── */

  .rmenu__dropdown {
    position: absolute;
    top: calc(100% + 2px);
    /* left / right / maxWidth are set via inline style by JS */
    max-height: 80vh;
    overflow-y: auto;
    background: ${({ themeProps, theme }) => themeProps?.bgColor || theme.colors.white};
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    z-index: 300;
    padding: 10px 0;
    display: flex;
    flex-direction: column;
  }

  .rmenu__dropdown-item {
    display: block;
    padding: ${({ themeProps }) => themeProps?.dropdownItemPadding || '11px 20px'};
    ${({ themeProps }) => (themeProps?.linkLineHeight ? `line-height: ${themeProps.linkLineHeight};` : '')}
    font-size: 15px;
    font-weight: 400;
    color: ${({ themeProps, theme }) => themeProps?.color || theme.colors.brandDark};
    text-decoration: none;
    white-space: nowrap;
    transition: color 0.15s;

    &:hover {
      color: ${({ themeProps, theme }) => themeProps?.hoverColor || theme.colors.brand};
    }
  }

  /* ── Mega menu (SHOP) ── */

  .rmenu__item--shop {
    position: relative;
  }

  .rmenu__mega {
    position: absolute;
    top: 100%;
    max-height: calc(100vh - 130px);
    overflow-y: auto;
    background: ${({ themeProps, theme }) => themeProps?.bgColor || theme.colors.white};
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
    z-index: 300;
    max-width: calc(100vw - 300px);
    display: flex;
    width: max-content;
  }

  .rmenu__mega-inner {
    padding: ${({ themeProps }) => themeProps?.megaPadding || '60px'};
    display: flex;
    flex-wrap: wrap;
    gap: ${({ themeProps }) => themeProps?.megaGap || '60px'};
    width: auto;

    &.brands {
      padding: ${({ themeProps }) => themeProps?.megaPaddingBrands || '48px 20px'};
      gap: 0;
      display: grid;
      grid-template-columns: repeat(4, 1fr);

      @media (max-width: 1550px) {
        grid-template-columns: repeat(3, 1fr);
      }
      @media (max-width: 1300px) {
        grid-template-columns: repeat(2, 1fr);
      }
      @media (max-width: 1080px) {
        grid-template-columns: repeat(1, 1fr);
      }
      @media (max-width: 990px) {
        grid-template-columns: repeat(3, 1fr);
      }
      @media (max-width: 866px) {
        grid-template-columns: repeat(2, 1fr);
      }
    }
  }

  .rmenu__col-heading {
    font-size: 14px;
    font-weight: 900;
    letter-spacing: 0.1em;
    color: ${({ themeProps, theme }) => themeProps?.headingColor || themeProps?.color || theme.colors.brandDark};
    text-transform: uppercase;
    margin: 0 0 12px;
    white-space: nowrap;
  }

  .rmenu__col-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: ${({ themeProps }) => themeProps?.linkGap || '5px'};
  }

  .rmenu__link {
    display: block;
    padding: ${({ themeProps }) => themeProps?.linkPadding || '6px 0'};
    ${({ themeProps }) => (themeProps?.linkLineHeight ? `line-height: ${themeProps.linkLineHeight};` : '')}
    font-size: 16px;
    font-weight: 400;
    color: ${({ themeProps, theme }) => themeProps?.color || theme.colors.brandDark};
    text-decoration: none;
    transition: color 0.15s;
    white-space: nowrap;

    &:hover {
      color: ${({ themeProps, theme }) => themeProps?.hoverColor || theme.colors.brand};
    }
  }

  .rmenu__link--shop-all {
    margin-top: ${({ themeProps }) => themeProps?.shopAllMarginTop || '14px'};
    font-size: 19px;
    font-weight: 900;
    letter-spacing: 0.05em;
    color: ${({ themeProps, theme }) => themeProps?.color || theme.colors.brandDark};
    text-transform: uppercase;

    &:hover {
      color: ${({ themeProps, theme }) => themeProps?.hoverColor || theme.colors.brand};
    }
  }
`;

export default StyledMainMenuUniversal;
